import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useBreakpointValue } from '@chakra-ui/react';
import { useStripe } from '@stripe/react-stripe-js';

import { PackageType } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';

import { PricingPlansDataDocument } from './graphql/queries/pricingPlansData.gql';
import machineConfig, { model, State } from './machine';
import { PlanSelection, TermsAndConditions } from './pages';
import PackagePayment from './pages/PackagePayment/PackagePayment';
import PackageRecap from './pages/PackageRecap/PackageRecap';

const PricingPlanComponent = withStateMachine<typeof model>(
  (props) => {
    const { machine, moduleData } = props;
    const [state, send] = machine;

    const stringifiedState = stringifyState<State>(state);
    const { isMobile, packageType, viewer } = moduleData;
    const referrer = viewer.referrer?.firstName;

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    switch (stringifiedState) {
      case State.PLAN_SELECTION:
        return (
          <PlanSelection
            appliedCoupon={state.context.selectedCoupon}
            isMobile={isMobile}
            moduleData={moduleData}
            onChoosePackage={(pack) =>
              send(model.events.SAVE_SELECTED_PACKAGE(pack))
            }
            onChoosePlan={(plan) => send(model.events.SAVE_SELECTED_PLAN(plan))}
            onPrev={onPrev}
            onShowDetails={() => send(model.events.SHOW_DETAILS())}
            referrer={referrer}
            setVariant={(variant) => send(model.events.SAVE_VARIANT(variant))}
            shouldShowPackagePriceWithVAT={
              moduleData.shouldShowPackagePriceWithVAT
            }
            variant={state.context.selectedVariant}
          />
        );

      case State.TERMS_AND_CONDITIONS: {
        if (!state.context.selectedPlan) {
          throw new Error(`No plan id in ${stringifiedState}`);
        }
        return (
          <TermsAndConditions
            isSavingChoice={state.matches(State.DONE)}
            onAgreementPress={() => send(model.events.NEXT())}
            onPrev={onPrev}
            plan={state.context.selectedPlan}
            shouldSeeProAndBusinessInsurance={
              moduleData.viewer.company.shouldSeeProAndBusinessPlans
            }
          />
        );
      }

      case State.INIT_PACKAGE_PAYMENT_INTENT:
      case State.SUBMIT_PLAN:
      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      case State.PACKAGE_RECAP: {
        if (!state.context.selectedPackage) {
          throw new Error(`No package in ${stringifiedState}`);
        }

        if (!packageType) {
          throw new Error(`Package recap is not available without packageType`);
        }

        return (
          <PackageRecap
            companyProfileId={moduleData.viewer.company.companyProfileId}
            coupon={state.context.selectedCoupon}
            onCouponApplied={(coupon) =>
              send(model.events.SUBMIT_COUPON(coupon))
            }
            onNext={() => send('NEXT')}
            onPrev={onPrev}
            packageType={packageType}
            providerPackageId={state.context.selectedPackage.providerPackageId}
            referrer={moduleData.viewer.referrer?.firstName ?? null}
            shouldShowPackagePriceWithVAT={
              moduleData.shouldShowPackagePriceWithVAT
            }
          />
        );
      }

      case State.PACKAGE_PAYMENT:
      case State.PACKAGE_PAYMENT_SEND: {
        if (!state.context.selectedPackage) {
          throw new Error(`No package in ${stringifiedState}`);
        }
        if (!state.context.paymentData?.clientSecret) {
          throw new Error(`No clientSecret in ${stringifiedState}`);
        }

        if (!packageType) {
          throw new Error(
            `Package payment is not available for without packageType`,
          );
        }

        return (
          <PackagePayment
            companyProfileId={moduleData.viewer.company.companyProfileId}
            coupon={state.context.selectedCoupon}
            isSubmittingPayment={state.matches(State.PACKAGE_PAYMENT_SEND)}
            onPay={(payload) => send(model.events.PAY(payload))}
            onPrev={() => send(model.events.PREV())}
            packageType={packageType}
            paymentSecret={state.context.paymentData.clientSecret}
            providerPackageId={state.context.selectedPackage.providerPackageId}
          />
        );
      }

      case State.PACKAGE_PAYMENT_SUCCESSFUL:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },

  {
    machineConfig,
  },
);

interface PricingPlanProps {
  packageType: PackageType | null;
  /**
   * If true, will display the package price with taxes included (ie TTC)
   */
  shouldShowPackagePriceWithVAT?: boolean;
}

/**
 * PricingPlan is a bento module that displays the pricing plan selection page.
 */
const PricingPlan = asBentoModule<PricingPlanProps>(
  ({ onDone, packageType, shouldShowPackagePriceWithVAT = false }) => {
    const { companyProfileId } = useCompanyContext();

    if (!companyProfileId) {
      throw new Error('No company context in pricing plan module');
    }

    const stripe = useStripe();

    if (!stripe) {
      throw new Error('Stripe is not available');
    }

    const query = useQuery(PricingPlansDataDocument, {
      variables: {
        companyProfileId,
      },
    });

    const isMobile = Boolean(
      useBreakpointValue(
        {
          base: true,
          lg: false,
        },
        // We need to pass an empty default breakpoint to get the right value in the first render (the initial value is 'base' by default)
        '',
      ),
    );

    return (
      <SuspenseQuery
        loader={<OBLoaderPage hideNavigation onPrev={() => null} />}
        query={query}
      >
        {(moduleData) => (
          <PricingPlanComponent
            moduleData={{
              ...moduleData,
              isMobile,
              packageType,
              shouldShowPackagePriceWithVAT,
              stripe,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

export default PricingPlan;
